import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Clipboard, Clock, Download, FileDown, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  createGuestDownloadRequest,
  getGuestDownloadRequestStatus,
  guestDownloadHref,
  type GuestDownloadArtifactRecord,
  type GuestDownloadRequestRecord,
  type GuestDownloadStatus
} from "@/api/public-download-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { isMailArchiverApp, MailArchiverIcon } from "@/components/mailarchiver-icon";
import { cn } from "@/lib/utils";

type ActiveGuestRequest = {
  requestId: string;
  requestCode: string;
  requestSecret: string;
  expiresAt: string;
};

type DownloadAppGroup = {
  appId: string;
  label: string;
  platforms: DownloadPlatformGroup[];
};

type DownloadPlatformGroup = {
  key: string;
  label: string;
  downloads: GuestDownloadArtifactRecord[];
};

const FRIENDLY_APP_NAMES: Record<string, string> = {
  mailarchiver: "MailArchiver"
};

const PLATFORM_ORDER: Record<string, number> = {
  "windows/amd64": 10,
  "windows/x64": 10,
  "win32/amd64": 10,
  "linux/amd64": 20,
  "linux/x64": 20,
  "darwin/arm64": 30,
  "macos/arm64": 30,
  "darwin/amd64": 40,
  "macos/amd64": 40
};

function formatDateTime(value?: string) {
  if (!value) return "нет данных";
  return new Date(value).toLocaleString("ru-RU");
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 Б";
  const units = ["Б", "КБ", "МБ", "ГБ"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function statusVariant(status: GuestDownloadStatus): "success" | "warning" | "destructive" | "outline" {
  if (status === "APPROVED") return "success";
  if (status === "PENDING") return "warning";
  if (status === "EXPIRED" || status === "REVOKED") return "destructive";
  return "outline";
}

function buildDownloadGroups(downloads: GuestDownloadArtifactRecord[]): DownloadAppGroup[] {
  const byApp = new Map<string, GuestDownloadArtifactRecord[]>();

  for (const download of downloads) {
    const existing = byApp.get(download.appId) ?? [];
    existing.push(download);
    byApp.set(download.appId, existing);
  }

  return [...byApp.entries()]
    .map(([appId, appDownloads]) => ({
      appId,
      label: formatAppLabel(appId),
      platforms: buildPlatformGroups(appDownloads)
    }))
    .sort((left, right) => left.label.localeCompare(right.label, "ru", { numeric: true }));
}

function buildPlatformGroups(downloads: GuestDownloadArtifactRecord[]): DownloadPlatformGroup[] {
  const byPlatform = new Map<string, GuestDownloadArtifactRecord[]>();

  for (const download of downloads) {
    const key = platformKey(download.os, download.arch);
    const existing = byPlatform.get(key) ?? [];
    existing.push(download);
    byPlatform.set(key, existing);
  }

  return [...byPlatform.entries()]
    .map(([key, platformDownloads]) => {
      const sorted = [...platformDownloads].sort(compareDownloadsByRecency);
      const first = sorted[0];
      return {
        key,
        label: first ? formatPlatformLabel(first.os, first.arch) : key,
        downloads: sorted
      };
    })
    .sort(comparePlatformGroups);
}

function formatAppLabel(appId: string): string {
  const normalized = appId.trim();
  if (!normalized) return "Программа";
  const friendly = FRIENDLY_APP_NAMES[normalized.toLowerCase()];
  if (friendly) return friendly;

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function platformKey(os: string, arch: string): string {
  return `${os.trim().toLowerCase()}/${arch.trim().toLowerCase()}`;
}

function formatPlatformLabel(os: string, arch: string): string {
  const key = platformKey(os, arch);
  if (key === "windows/amd64" || key === "windows/x64" || key === "win32/amd64") return "Windows x64";
  if (key === "linux/amd64" || key === "linux/x64") return "Linux x64";
  if (key === "darwin/arm64" || key === "macos/arm64") return "macOS Apple";
  if (key === "darwin/amd64" || key === "macos/amd64") return "macOS Intel";

  return `${os}/${arch}`;
}

function comparePlatformGroups(left: DownloadPlatformGroup, right: DownloadPlatformGroup): number {
  const leftOrder = PLATFORM_ORDER[left.key] ?? 1000;
  const rightOrder = PLATFORM_ORDER[right.key] ?? 1000;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;
  return left.label.localeCompare(right.label, "ru", { numeric: true });
}

function compareDownloadsByRecency(left: GuestDownloadArtifactRecord, right: GuestDownloadArtifactRecord): number {
  const rightTime = releaseTime(right);
  const leftTime = releaseTime(left);
  if (rightTime !== leftTime) return rightTime - leftTime;
  const versionOrder = right.version.localeCompare(left.version, undefined, { numeric: true, sensitivity: "base" });
  if (versionOrder !== 0) return versionOrder;
  if (left.defaultDownload !== right.defaultDownload) return left.defaultDownload ? -1 : 1;
  const leftKindOrder = left.kind === "installer" ? 0 : 1;
  const rightKindOrder = right.kind === "installer" ? 0 : 1;
  return leftKindOrder - rightKindOrder;
}

function releaseTime(download: GuestDownloadArtifactRecord): number {
  const time = Date.parse(download.publishedAt || "");
  return Number.isFinite(time) ? time : 0;
}

export function PublicDownloadPage() {
  const [activeRequest, setActiveRequest] = useState<ActiveGuestRequest | null>(null);

  const status = useQuery({
    queryKey: ["public-download", activeRequest?.requestId, activeRequest?.requestSecret],
    queryFn: () => getGuestDownloadRequestStatus(activeRequest!.requestId, activeRequest!.requestSecret),
    enabled: Boolean(activeRequest),
    refetchInterval: (query) => (query.state.data?.status === "PENDING" ? 2500 : false),
    refetchOnWindowFocus: true
  });

  const createRequest = useMutation({
    mutationFn: createGuestDownloadRequest,
    onSuccess: (request) => {
      if (!request.requestSecret) {
        toast.error("Сервер не вернул secret для запроса");
        return;
      }
      setActiveRequest({
        requestId: request.requestId,
        requestCode: request.requestCode,
        requestSecret: request.requestSecret,
        expiresAt: request.expiresAt
      });
      toast.success("Код создан");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Не удалось создать код");
    }
  });

  const current = status.data ?? activeRequestToRecord(activeRequest);
  const downloads = current?.downloads ?? [];
  const downloadGroups = useMemo(() => buildDownloadGroups(downloads), [downloads]);
  const canCreate = !activeRequest || current?.status === "EXPIRED" || current?.status === "REVOKED";

  const copyCode = async () => {
    if (!activeRequest) return;
    await navigator.clipboard.writeText(activeRequest.requestCode);
    toast.success("Код скопирован");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b bg-panel">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Doctor Server Licensing</h1>
            <p className="text-xs text-muted-foreground">Платформа лицензирования</p>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-4 px-4 py-5 sm:py-8">
        <section className="grid gap-4 rounded-md border bg-panel p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <KeyRound className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold">Код доступа</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Передайте код администратору. После подтверждения здесь появятся доступные файлы.
              </p>
            </div>
          </div>

          {current ? (
            <GuestRequestCard
              request={current}
              onCopyCode={copyCode}
              refreshing={status.isFetching}
              onRefresh={() => void status.refetch()}
            />
          ) : (
            <div className="rounded-md bg-muted p-4 text-sm text-muted-foreground">
              Код еще не создан.
            </div>
          )}

          {canCreate ? (
            <Button className="w-full sm:w-fit" onClick={() => createRequest.mutate()} disabled={createRequest.isPending}>
              <KeyRound className="h-4 w-4" />
              Получить код
            </Button>
          ) : null}
        </section>

        {activeRequest && current?.status === "PENDING" ? (
          <section className="rounded-md border bg-panel p-4">
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-warning" />
              <span>Ожидание подтверждения</span>
            </div>
          </section>
        ) : null}

        {activeRequest && current?.status === "APPROVED" ? (
          <section className="grid gap-3 rounded-md border bg-panel p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <h2 className="text-base font-semibold">Доступные файлы</h2>
            </div>
            {downloads.length > 0 ? (
              <div className="grid gap-4">
                {downloadGroups.map((group) => (
                  <DownloadAppGroupSection key={group.appId} group={group} requestSecret={activeRequest.requestSecret} />
                ))}
              </div>
            ) : (
              <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                Подтверждение получено, список файлов обновляется.
              </div>
            )}
          </section>
        ) : null}
      </main>
      <footer className="mx-auto flex max-w-5xl justify-center px-4 pb-6">
        <Link to="/login" className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          Вход для сотрудников
        </Link>
      </footer>
    </div>
  );
}

function GuestRequestCard({
  request,
  onCopyCode,
  refreshing,
  onRefresh
}: {
  request: GuestDownloadRequestRecord;
  onCopyCode: () => void;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="rounded-md border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(request.status)}>{request.status}</Badge>
            <span className="text-xs text-muted-foreground">до {formatDateTime(request.expiresAt)}</span>
          </div>
          <div className="mt-3 font-mono text-3xl font-semibold tracking-normal sm:text-4xl">
            {request.requestCode}
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="icon" aria-label="Скопировать код" onClick={onCopyCode}>
            <Clipboard className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label="Обновить статус" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function DownloadAppGroupSection({ group, requestSecret }: { group: DownloadAppGroup; requestSecret: string }) {
  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-2">
        {isMailArchiverApp(group.appId) ? (
          <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />
        ) : (
          <FileDown className="h-5 w-5 text-muted-foreground" />
        )}
        <h3 className="text-sm font-semibold">{group.label}</h3>
      </div>
      <div className="grid gap-2">
        {group.platforms.flatMap((platform) =>
          platform.downloads.map((artifact) => (
            <DownloadCard
              key={artifact.artifactId}
              artifact={artifact}
              platformLabel={platform.label}
              requestSecret={requestSecret}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DownloadCard({
  artifact,
  platformLabel,
  requestSecret
}: {
  artifact: GuestDownloadArtifactRecord;
  platformLabel: string;
  requestSecret: string;
}) {
  const href = useMemo(() => guestDownloadHref(artifact.downloadUrl, requestSecret), [artifact.downloadUrl, requestSecret]);
  return (
    <div className="flex flex-col gap-3 rounded-md border bg-background p-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          {isMailArchiverApp(artifact.appId) ? (
            <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />
          ) : (
            <FileDown className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{platformLabel}</p>
            <Badge variant={artifact.channel.toLowerCase() === "stable" ? "success" : "outline"}>{artifact.channel}</Badge>
            <Badge>{artifact.version}</Badge>
            {artifact.kind === "installer" ? <Badge variant="success">Installer</Badge> : null}
            {artifact.defaultDownload ? <Badge variant="outline">Default</Badge> : null}
          </div>
          <p className="mt-1 break-words text-xs text-muted-foreground">
            {artifact.filename} · {formatBytes(artifact.sizeBytes)}
          </p>
          <p className="mt-1 truncate font-mono text-[11px] text-muted-foreground">{artifact.sha256}</p>
        </div>
      </div>
      <Button asChild className="w-full sm:w-auto">
        <a href={href}>
          <Download className="h-4 w-4" />
          Скачать
        </a>
      </Button>
    </div>
  );
}

function activeRequestToRecord(activeRequest: ActiveGuestRequest | null): GuestDownloadRequestRecord | null {
  if (!activeRequest) return null;
  return {
    requestId: activeRequest.requestId,
    requestCode: activeRequest.requestCode,
    requestSecret: activeRequest.requestSecret,
    status: "PENDING",
    expiresAt: activeRequest.expiresAt
  };
}
